'use client';
import React, { useRef } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/all';
import { Lottie, LottieRefCurrentProps } from '@/components/LottieWrapper';
import BuildTestingLottie from '@assets/lottie/buildtesting.json';
import BuildCycle from '@assets/images/develocity/build-cycle.png';
import { ChevronRightIcon } from '@heroicons/react/20/solid';
import Title from '@/components/Title';
import { useGSAP } from '@gsap/react';
import { useAnimatedNavbarHeight } from '@/lib/hooks/useAnimatedNavbarHeight';
import { MD_BREAKPOINT } from '@/lib/constants';

interface BuildTestProps {
  title?: string;
  subtitle?: string;
  section: Array<sectionProps>;
}

interface sectionProps {
  title?: string;
  text?: string;
  href?: string;
}

interface BuildTestSectionProps {
  data: BuildTestProps;
}

const renderBuildTestSection = ({ title, text, href }: sectionProps, index: number) => {
  return (
    <div key={index} className={`flex flex-col p-0`}>
      <h4 className="text-h6 mb-2 text-white">{title}</h4>
      <h5 className="text-eyebrow-strong mb-4 text-gray-4 md:mb-6">{text}</h5>
      <Link
        href={`${href}`}
        className="underline-text-transition-blue group flex items-center justify-center gap-1 self-end text-white md:self-start"
        target="_blank"
      >
        <span className="text-body">Learn More</span>
        <ChevronRightIcon className="inline-block size-6 group-hover:translate-x-1" />
      </Link>
    </div>
  );
};

export const AccelerateAndBuildSection: React.FC<BuildTestSectionProps> = ({ data }) => {
  const { title, subtitle, section } = data;
  const ScrollRef = useRef(null);
  const refLottie = useRef<LottieRefCurrentProps>(null);
  const refBox = useRef<HTMLDivElement>(null);
  const startingFrame = 20;

  const [navPlusBannerHeight] = useAnimatedNavbarHeight();

  useGSAP(
    () => {
      gsap.registerPlugin(ScrollTrigger);

      const mm = gsap.matchMedia();

      mm.add(`(min-width: ${MD_BREAKPOINT}px)`, () => {
        const playhead = { frame: startingFrame };

        const tl = gsap.timeline({
          scrollTrigger: {
            trigger: refBox.current,
            pin: true,
            start: () => `top-=5% center-=15%+=${navPlusBannerHeight})`,
            endTrigger: ScrollRef.current,
            end: '67% center-=15%',
            scrub: true,
            markers: false,
            invalidateOnRefresh: true,
          },
        });

        tl.to(playhead, {
          ease: 'none',
          frame: 80,
          onUpdate: () => {
            if (refLottie.current?.animationLoaded) {
              refLottie.current.goToAndStop(playhead.frame, true);
            }
          },
        });
      });
    },
    { scope: ScrollRef, dependencies: [navPlusBannerHeight], revertOnUpdate: true },
  );

  return (
    <div className="mx-auto flex h-full max-w-[1440px] flex-col px-4 md:h-full lg:px-10" ref={ScrollRef}>
      <Title title={title} subtitle={subtitle} className="title-container" color="white" />
      <div className="relative flex flex-col items-center justify-start gap-5 px-0 md:gap-10 lg:h-[100rem] lg:flex-row lg:px-10">
        <div className="flex h-auto w-full items-center justify-center self-start lg:basis-1/2">
          <div ref={refBox} className="hidden w-[36rem] lg:block">
            <Lottie
              lottieRef={refLottie}
              animationData={BuildTestingLottie}
              autoPlay={false}
              loop={false}
              initialSegment={[0, startingFrame]}
            />
          </div>
          <div className="flex h-full w-full items-center justify-center px-4 lg:hidden">
            <Image alt={'build cycle quadrants'} width={400} height={400} src={BuildCycle} />
          </div>
        </div>
        <div className="text relative flex h-full w-full flex-1 flex-col justify-between gap-10 px-3 md:px-10 lg:w-1/2 lg:gap-0 lg:px-0 lg:pb-72 lg:pt-36">
          {section.map((item, idx) => renderBuildTestSection(item, idx))}
        </div>
      </div>
    </div>
  );
};
